import indexDbHelper from "@/helpers/indexDbHelper";
import { calculateTableOrderTax } from "../../helpers/tableOrderTaxCalculator";
import { applyPromotionsToCart } from "@/services/promotionService";
import {
  getOrCreateIdempotencyKey,
  clearIdempotencyKey
} from "@/services/localStorageService";

const CALCULATED_CART_FIELDS = [
  "discount",
  "wholeDiscount",
  "itemTotal",
  "itemTotalWithoutDiscount",
  "promotionLabels",
  "tax",
  "tax_items",
  "tax_total",
  "subtotal",
  "grand_total"
];

function stripCalculatedCartFields(item) {
  const cleanedItem = { ...item };
  CALCULATED_CART_FIELDS.forEach((field) => {
    delete cleanedItem[field];
  });
  return cleanedItem;
}

/**
 * Order Module
 * Manages cart items, order configuration, and order calculations
 */
export const order = {
  namespaced: true,
  state: {
    // Legacy order items (for backward compatibility)
    items: [],

    // Cart items (Lumina Eats)
    cartItems: [],

    // Order configuration
    orderConfig: {
      type: "pickup",
      timing: "asap"
    },

    // Order calculations
    subtotal: 0,
    tipAmount: 0,
    totalTax: 0,
    discountTotal: 0,
    appliedPromotions: [],

    // Order details
    serve_at: "",
    delivery_address: {
      country: "",
      state: "",
      first_name: "",
      last_name: "",
      city: "",
      postal_code: "",
      phone: "",
      address_1: "",
      address_2: ""
    }
  },
  getters: {
    // Legacy items
    getItems: (state) => state.items,

    // Cart items
    getCartItems: (state) => state.cartItems,
    totalItems: (state) => {
      return state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
    },

    // Order config
    getOrderConfig: (state) =>
      state.orderConfig || { type: "pickup", timing: "asap" },

    // Calculations
    getSubtotal: (state) => state.subtotal,
    getTipAmount: (state) => state.tipAmount,
    getTotalTax: (state) => state.totalTax,
    getDiscountTotal: (state) => state.discountTotal,
    getAppliedPromotions: (state) => state.appliedPromotions,
    subtotal: (state) => state.subtotal,

    // Order details
    getServeAt: (state) => state.serve_at,
    getDeliveryAddress: (state) => state.delivery_address
  },
  mutations: {
    // Legacy items
    SET_ITEMS(state, payload) {
      state.items = payload;
    },
    ADD_ITEM(state, payload) {
      state.items.push(payload);
      indexDbHelper.saveToDb("orderItems", state.items);
    },
    UPDATE_ITEM(state, payload) {
      const Vue = require("vue").default;
      Vue.set(state.items, payload.index, payload.item);
      indexDbHelper.saveToDb("orderItems", state.items);
    },
    REMOVE_ITEM(state, payload) {
      indexDbHelper.deleteSingleInstance(
        "orderItems",
        state.items[payload].local_id
      );
      state.items.splice(payload, 1);
    },

    // Cart items
    SET_CART_ITEMS(state, items) {
      state.cartItems = items;
      // Save to IndexDB when setting all items
      // If items array is empty, clear IndexDB
      if (!items || items.length === 0) {
        // Clear IndexDB when cart is empty
        indexDbHelper.clearAllData("orderItems").catch((err) => {
          console.error("Error clearing cartItems from IndexDB:", err);
        });
      } else {
        // Save items to IndexDB when not empty
        const itemsForDb = items.map((item) => ({
          ...item,
          local_id: item.local_id || item.cartId || `${item.id}-${Date.now()}`
        }));
        const validItems = itemsForDb.filter((item) => item.local_id);
        if (validItems.length > 0) {
          indexDbHelper.saveToDb("orderItems", validItems).catch((err) => {
            console.error("Error saving cartItems to IndexDB:", err);
          });
        }
      }
    },
    ADD_TO_CART(state, cartItem) {
      // Ensure local_id exists for IndexDB (use cartId if local_id doesn't exist)
      const itemToAdd = {
        ...cartItem,
        local_id:
          cartItem.local_id || cartItem.cartId || `${cartItem.id}-${Date.now()}`
      };
      state.cartItems.push(itemToAdd);
      // Save all cartItems to IndexDB
      const itemsForDb = state.cartItems
        .map((item) => ({
          ...item,
          local_id: item.local_id || item.cartId
        }))
        .filter((item) => item.local_id); // Only save items with valid local_id

      if (itemsForDb.length > 0) {
        indexDbHelper.saveToDb("orderItems", itemsForDb).catch((err) => {
          console.error("Error saving cartItems to IndexDB:", err);
        });
      }
    },
    UPDATE_QUANTITY(state, { cartId, delta }) {
      const item = state.cartItems.find((item) => item.cartId === cartId);
      if (item) {
        item.quantity = Math.max(1, item.quantity + delta);
        // Save all cartItems to IndexDB after update
        const itemsForDb = state.cartItems
          .map((item) => ({
            ...item,
            local_id: item.local_id || item.cartId
          }))
          .filter((item) => item.local_id); // Only save items with valid local_id

        if (itemsForDb.length > 0) {
          indexDbHelper.saveToDb("orderItems", itemsForDb).catch((err) => {
            console.error("Error saving cartItems to IndexDB:", err);
          });
        }
      }
    },
    REMOVE_ITEM_CART(state, cartId) {
      const itemToRemove = state.cartItems.find(
        (item) => item.cartId === cartId
      );
      if (itemToRemove) {
        // Delete from IndexDB using local_id or cartId
        const idToDelete = itemToRemove.local_id || itemToRemove.cartId;
        if (idToDelete) {
          indexDbHelper.deleteSingleInstance("orderItems", idToDelete);
        }
      }
      state.cartItems = state.cartItems.filter(
        (item) => item.cartId !== cartId
      );
    },
    REPLACE_CART_ITEM(state, { cartId, cartItem }) {
      const index = state.cartItems.findIndex((item) => item.cartId === cartId);
      if (index === -1) return;
      const existing = state.cartItems[index];
      const itemToSave = {
        ...cartItem,
        cartId: existing.cartId,
        local_id: existing.local_id || existing.cartId
      };
      state.cartItems.splice(index, 1, itemToSave);
      const itemsForDb = state.cartItems
        .map((item) => ({
          ...item,
          local_id: item.local_id || item.cartId
        }))
        .filter((item) => item.local_id);
      if (itemsForDb.length > 0) {
        indexDbHelper.saveToDb("orderItems", itemsForDb).catch((err) => {
          console.error("Error saving cartItems to IndexDB:", err);
        });
      }
    },

    // Order config
    SET_ORDER_CONFIG(state, config) {
      state.orderConfig = { ...state.orderConfig, ...config };
    },

    // Calculations
    RECALCULATE(state, payload = {}) {
      let storeConfig = {};

      if (payload.rootGetters) {
        const store = payload.rootGetters.getStore || {};
        storeConfig = {
          taxRound: payload.rootGetters.getTaxRound || "",
          isStoreTaxInclusive: payload.rootGetters.isStoreTaxInclusive || false,
          taxRates: store.tax_rates || []
        };
      } else {
        const rootState = payload.rootState || {};
        const storeState = rootState.store || {};
        storeConfig = {
          taxRound: storeState.tax_round || "",
          isStoreTaxInclusive: storeState.isStoreTaxInclusive || false,
          taxRates: storeState.store?.tax_rates || []
        };
      }

      const useCartItems = state.cartItems.length > 0;
      const activeItems = useCartItems ? state.cartItems : state.items;

      // Use the robust calculator from tableOrder
      const { totalTax, itemsWithTax, subtotal } = calculateTableOrderTax(
        activeItems,
        storeConfig
      );

      // Update state
      // Note: We keep using calculateSubtotal for subtotal if we want to preserve specific logic,
      // but the user asked to match tableOrder. tableOrder uses the subtotal from the calculator.
      // However, regular order might rely on subtotal being calculated differently (e.g. without discounts subtracted).
      // Let's stick to the calculator's output for consistency with tableOrder as requested,
      // BUT if we notice issues with subtotal, we might revert that part.
      // For now, let's use the calculator's tax and update items, but maybe preserve subtotal calculation?
      // tableOrder.js does: state.subtotal = subtotal;
      // Let's try to match tableOrder fully.
      state.subtotal = subtotal;
      state.totalTax = totalTax;

      // Update items with calculated tax info
      activeItems.forEach((item, index) => {
        const itemWithTax = itemsWithTax[index];
        if (itemWithTax) {
          // Update item in place
          // We need to be careful with Vue 2 reactivity if these are new properties,
          // but since we are replacing existing props or adding them, simple assignment might work
          // if the object is already reactive.
          // tableOrder.js uses Object.assign.
          Object.assign(item, {
            tax: itemWithTax.tax,
            tax_items: itemWithTax.tax_items,
            product: itemWithTax.product,
            tax_rate_value: itemWithTax.tax_rate_value
          });
        }
      });
    },
    SET_PROMOTION_SUMMARY(
      state,
      { appliedPromotions = [], discountTotal = 0 }
    ) {
      state.appliedPromotions = appliedPromotions;
      state.discountTotal = discountTotal;
    },
    setTipAmount(state, payload) {
      state.tipAmount = payload;
    },

    // Order details
    setServeAt(state, payload) {
      state.serve_at = payload;
    },
    setDeliveryAddress(state, payload) {
      state.delivery_address = payload;
    },

    // Search places
    searchedPlaces(state, searchedPlaces) {
      state.searchedPlaces = searchedPlaces;
    }
  },
  actions: {
    // ============================================
    // BUSINESS LOGIC ACTIONS
    // ============================================

    /**
     * Initializes cart from IndexDB
     * Business logic: Initialize IndexDB, restore cart items, restore legacy items
     */
    async initializeCart({ commit, state, rootGetters, dispatch }) {
      try {
        // 1. Initialize IndexDB
        await indexDbHelper.initialize();

        // 2. Restore cart from IndexDB (if cart is empty)
        const cartItems = state.cartItems || [];
        if (cartItems.length === 0) {
          try {
            const dbItems = await indexDbHelper.getDbData("orderItems");

            if (dbItems && Array.isArray(dbItems) && dbItems.length > 0) {
              // Restore cartItems from IndexDB
              const restoredCartItems = dbItems.map((item) => {
                const restoredItem = stripCalculatedCartFields(item);
                // Ensure product object has tax_rates for tax calculation
                const product = restoredItem.product || {
                  id: restoredItem.id,
                  tax_rates: restoredItem.tax_rates || []
                };

                return {
                  ...restoredItem,
                  cartId:
                    restoredItem.cartId ||
                    restoredItem.local_id ||
                    `${restoredItem.id}-${Date.now()}`,
                  local_id:
                    restoredItem.local_id ||
                    restoredItem.cartId ||
                    `${restoredItem.id}-${Date.now()}`,
                  selectedModifiers:
                    restoredItem.selectedModifiers ||
                    restoredItem.modifiers ||
                    [],
                  // Ensure product object has tax_rates for tax calculation
                  product: product,
                  // Ensure tax_status is preserved or calculated (same as root)
                  tax_status:
                    restoredItem.tax_status !== undefined
                      ? restoredItem.tax_status
                      : restoredItem.tax_rate_value
                      ? 0
                      : 1
                };
              });

              // Set items in state
              commit("SET_CART_ITEMS", restoredCartItems);

              // Apply promotions and recalculate tax
              await dispatch("applyPromotions");
              commit("RECALCULATE", { rootGetters });
            }
          } catch (error) {
            console.error("Error loading cartItems from IndexDB:", error);
          }
        }

        // 3. Legacy items restoration (backward compatibility)
        if (state.items.length === 0 && state.cartItems.length === 0) {
          try {
            const items = await indexDbHelper.getDbData("orderItems");
            if (items.length > 0) {
              items.forEach((item) => {
                commit("ADD_ITEM", item);
              });
            }
          } catch (error) {
            console.error("Error restoring legacy items:", error);
          }
        }
      } catch (error) {
        console.error("Failed to initialize cart:", error);
      }
    },

    /**
     * Applies active promotion rules from the dashboard to the current cart.
     */
    async applyPromotions({ commit, state, rootGetters }) {
      const promotions = rootGetters.getPromotions || [];
      const result = applyPromotionsToCart(state.cartItems, promotions, {
        timezone: rootGetters.getStoreTimezone,
        serveAt: rootGetters.getServeAt,
        orderConfig: state.orderConfig
      });

      if (state.cartItems.length > 0) {
        commit("SET_CART_ITEMS", result.items);
      }
      commit("SET_PROMOTION_SUMMARY", {
        appliedPromotions: result.appliedPromotions,
        discountTotal: result.discountTotal
      });
    },

    /**
     * Clears cart completely
     * Business logic: Clear IndexDB, clear state, recalculate
     */
    // eslint-disable-next-line no-unused-vars
    async clearCart({ commit, dispatch, rootGetters }) {
      try {
        // 1. Clear IndexDB
        await indexDbHelper.clearAllData("orderItems");

        // 2. Clear state
        commit("SET_CART_ITEMS", []);
        commit("SET_ITEMS", []);
        commit("SET_PROMOTION_SUMMARY", {
          appliedPromotions: [],
          discountTotal: 0
        });

        // 3. Recalculate (should be 0 now)
        commit("RECALCULATE", { rootGetters });
      } catch (error) {
        console.error("Failed to clear cart:", error);
        throw error;
      }
    },

    /**
     * Submits order
     * Business logic: Build payload, call service, clear cart, update state
     * @param {Object} context - Vuex context
     * @param {Object} params - Order submission parameters
     */
    // eslint-disable-next-line no-unused-vars
    async submitOrder({ commit, getters, rootGetters, dispatch }, params) {
      // eslint-disable-next-line no-unused-vars
      const {
        buildOrderPayload,
        submitOrder: submitOrderService
      } = await import("@/services/orderService");

      try {
        const unavailableItems = getters.getCartItems.filter((item) =>
          rootGetters["store/isProduct86d"]?.(item.id || item.product?.id)
        );
        if (unavailableItems.length) {
          throw new Error(
            "One or more items in your cart are no longer available."
          );
        }

        // Build payload (lógica de negocio)
        // Use tipAmount from params if provided (from checkout), otherwise fall back to store getter
        const tipAmount =
          params.tipAmount != null ? params.tipAmount : getters.getTipAmount;

        const payload = buildOrderPayload({
          storeAlias: rootGetters.getStoreAlias,
          items: getters.getCartItems,
          subtotal: getters.subtotal,
          discountTotal: getters.getDiscountTotal,
          totalTax: getters.getTotalTax,
          tipAmount: tipAmount,
          deliveryFee: 0,
          orderConfig: getters.getOrderConfig,
          store: rootGetters.getStore,
          customer: params.customer,
          smsConsent: params.smsConsent,
          diningOptionName: rootGetters.getDiningOptionName,
          diningOptionObject: rootGetters.getDiningOptionObject,
          serveAt: getters.getServeAt,
          timezone: rootGetters.getStoreTimezone,
          deliveryAddress: getters.getDeliveryAddress,
          paymentTypeId: params.paymentTypeId,
          convenienceTotal: params.convenienceTotal,
          paymentDetails: params.paymentDetails,
          // Stable per-attempt key; reused across retries so the backend dedupes.
          idempotencyKey: getOrCreateIdempotencyKey()
        });

        const response = await submitOrderService(payload);
        // Order is in: this attempt is done, so retire the key (next order = new key).
        clearIdempotencyKey();
        await dispatch("clearCart");
        commit("setTipAmount", 0);
        commit("setDeliveryAddress", {
          country: "",
          state: "",
          first_name: "",
          last_name: "",
          city: "",
          postal_code: "",
          phone: "",
          address_1: "",
          address_2: ""
        });
        return response;
      } catch (error) {
        console.error("Failed to submit order:", error);
        // Keep the key ONLY when we never got a server response (network error /
        // timeout) — the charge may have gone through, so a retry must reuse the
        // key to dedupe. For any received HTTP response (e.g. a decline) or a
        // pre-request error, the attempt is concluded, so rotate the key.
        if (error !== undefined && error !== null) {
          clearIdempotencyKey();
        }
        throw error;
      }
    },

    // ============================================
    // SIMPLE ACTIONS (Delegation to mutations)
    // ============================================

    // Legacy items
    setItems({ commit, rootGetters }, payload) {
      commit("SET_ITEMS", payload);
      commit("RECALCULATE", { rootGetters });
    },
    addItem({ commit, rootGetters }, payload) {
      commit("ADD_ITEM", payload);
      commit("RECALCULATE", { rootGetters });
    },
    updateItem({ commit, rootGetters }, payload) {
      commit("UPDATE_ITEM", payload);
      commit("RECALCULATE", { rootGetters });
    },
    removeItem({ commit, rootGetters }, payload) {
      commit("REMOVE_ITEM", payload);
      commit("RECALCULATE", { rootGetters });
    },

    // Cart items
    async addToCart(
      { commit, state, rootGetters, dispatch },
      { item, quantity, note, modifiers }
    ) {
      if (rootGetters["store/isProduct86d"]?.(item.id)) {
        throw new Error(`${item.name || "This item"} is unavailable.`);
      }

      const selectedModifiers = modifiers || [];

      // Helper function to normalize note for comparison (trim and lowercase)
      const normalizeNote = (noteValue) => {
        return (noteValue || "").trim().toLowerCase();
      };

      // Helper function to get modifier ID for comparison
      const getModifierId = (mod) => {
        // Try different possible ID fields
        return mod.oryginal || mod.id || mod.original || null;
      };

      // Helper function to compare modifiers arrays exactly
      const compareModifiers = (mods1, mods2) => {
        // Both empty arrays are equal
        if (mods1.length === 0 && mods2.length === 0) return true;

        // Different lengths means different modifiers
        if (mods1.length !== mods2.length) return false;

        // If both are empty, they're equal
        if (mods1.length === 0) return true;

        // Sort by ID to ensure consistent comparison
        const sorted1 = [...mods1]
          .map((mod) => ({ id: getModifierId(mod), mod }))
          .filter((m) => m.id !== null)
          .sort((a, b) => {
            const idA = String(a.id);
            const idB = String(b.id);
            return idA < idB ? -1 : idA > idB ? 1 : 0;
          });

        const sorted2 = [...mods2]
          .map((mod) => ({ id: getModifierId(mod), mod }))
          .filter((m) => m.id !== null)
          .sort((a, b) => {
            const idA = String(a.id);
            const idB = String(b.id);
            return idA < idB ? -1 : idA > idB ? 1 : 0;
          });

        // If filtered lengths don't match, they're different
        if (sorted1.length !== sorted2.length) return false;

        // Compare each modifier by ID
        return sorted1.every((mod1, index) => {
          const mod2 = sorted2[index];
          return String(mod1.id) === String(mod2.id);
        });
      };

      // Check if item with same id, exact same modifiers, and exact same note already exists
      const existingItem = state.cartItems.find((cartItem) => {
        // Must be the same item ID
        if (cartItem.id !== item.id) {
          return false;
        }

        // Must have the same modifiers (exact match)
        const cartModifiers = cartItem.selectedModifiers || [];
        if (!compareModifiers(cartModifiers, selectedModifiers)) {
          return false;
        }

        // Must have the same note (exact match after normalization)
        const cartNote = normalizeNote(cartItem.note);
        const newNote = normalizeNote(note);
        if (cartNote !== newNote) {
          return false;
        }

        // All conditions met - this is the same item
        return true;
      });

      if (existingItem) {
        // Item already exists with exact same modifiers and note, increment quantity
        commit("UPDATE_QUANTITY", {
          cartId: existingItem.cartId,
          delta: quantity
        });
      } else {
        const itemId = `${item.id}-${Date.now()}`;

        // Get tax rates from store to map tax_rate_value to tax_rates
        const store = rootGetters.getStore || {};
        const taxRates = store.tax_rates || [];

        // Ensure product object has tax_rates for tax calculation
        // Product from store has tax_rates directly on the item object
        // We need to create a product object with tax_rates for tax calculation
        let itemTaxRates =
          item.tax_rates ||
          (item.product && item.product.tax_rates
            ? item.product.tax_rates
            : []);

        // If item has tax_rate_value but no tax_rates array, map tax_rate_value to tax rate ID
        if (
          itemTaxRates.length === 0 &&
          item.tax_rate_value != null &&
          taxRates.length > 0
        ) {
          // Find tax rate ID that matches the tax_rate_value
          const matchingTaxRate = taxRates.find(
            (tr) => Math.abs(tr.rate - item.tax_rate_value) < 0.01
          );
          if (matchingTaxRate) {
            itemTaxRates = [matchingTaxRate.id];
          }
        }

        const product = {
          id: item.id,
          tax_rates: itemTaxRates
        };

        const cartItem = {
          ...item,
          cartId: itemId,
          local_id: itemId, // Add local_id for IndexDB compatibility
          quantity,
          note: note || "",
          selectedModifiers,
          // Ensure modifiers exists for tax calculations (same as root)
          modifiers: selectedModifiers,
          // Ensure product object has tax_rates for tax calculation
          product: product,
          // Ensure tax_rate_value is preserved (comes directly from product)
          tax_rate_value: item.tax_rate_value || 0,
          // Tax status: 0 = taxable, 1 = non-taxable (same as root)
          tax_status: item.tax_rate_value ? 0 : 1
        };
        commit("ADD_TO_CART", cartItem);
      }

      // Apply promotions and recalculate tax after cart changes
      await dispatch("applyPromotions");
      commit("RECALCULATE", { rootGetters });

      // Open cart if on mobile or sidebar not visible
      if (window.innerWidth < 1024 || !rootGetters.isSidebarVisible) {
        commit("SET_CART_OPEN", true, { root: true });
      }
    },
    async updateQuantity({ commit, rootGetters, dispatch }, { cartId, delta }) {
      commit("UPDATE_QUANTITY", { cartId, delta });
      // Apply promotions and recalculate tax after quantity change
      await dispatch("applyPromotions");
      commit("RECALCULATE", { rootGetters });
    },
    async removeItemCart({ commit, state, rootGetters, dispatch }, cartId) {
      commit("REMOVE_ITEM_CART", cartId);
      // Apply promotions and recalculate tax after item removal
      await dispatch("applyPromotions");
      commit("RECALCULATE", { rootGetters });
      if (state.cartItems.length === 0) {
        commit("SET_CART_OPEN", false, { root: true });
      }
    },
    async updateCartItem(
      { commit, state, rootGetters, dispatch },
      { cartId, item, quantity, note, modifiers }
    ) {
      const existing = state.cartItems.find((i) => i.cartId === cartId);
      if (!existing) return;
      if (rootGetters["store/isProduct86d"]?.(item.id)) {
        throw new Error(`${item.name || "This item"} is unavailable.`);
      }

      const selectedModifiers = modifiers || [];
      const store = rootGetters.getStore || {};
      const taxRates = store.tax_rates || [];
      let itemTaxRates =
        item.tax_rates ||
        (item.product && item.product.tax_rates ? item.product.tax_rates : []);
      if (
        itemTaxRates.length === 0 &&
        item.tax_rate_value != null &&
        taxRates.length > 0
      ) {
        const matchingTaxRate = taxRates.find(
          (tr) => Math.abs(tr.rate - item.tax_rate_value) < 0.01
        );
        if (matchingTaxRate) itemTaxRates = [matchingTaxRate.id];
      }
      const product = { id: item.id, tax_rates: itemTaxRates };
      const cartItem = {
        ...item,
        cartId: existing.cartId,
        local_id: existing.local_id || existing.cartId,
        quantity,
        note: note || "",
        selectedModifiers,
        modifiers: selectedModifiers,
        product,
        tax_rate_value: item.tax_rate_value || 0,
        tax_status: item.tax_rate_value ? 0 : 1
      };
      commit("REPLACE_CART_ITEM", { cartId, cartItem });
      await dispatch("applyPromotions");
      commit("RECALCULATE", { rootGetters });
      if (window.innerWidth < 1024 || !rootGetters.isSidebarVisible) {
        commit("SET_CART_OPEN", true, { root: true });
      }
    },

    // Order config
    async setOrderConfig({ commit, dispatch, rootGetters }, config) {
      commit("SET_ORDER_CONFIG", config);
      await dispatch("applyPromotions");
      commit("RECALCULATE", { rootGetters });
    },

    /**
     * Sets default schedule values if store is in schedule-only mode
     * Called after location change to ensure scheduledDay and scheduledTime are set
     * @param {Object} context - Vuex context
     */
    async setDefaultScheduleIfNeeded({
      commit,
      rootGetters,
      rootState,
      dispatch
    }) {
      try {
        // Import useSelectedStore dynamically to avoid circular dependencies
        const { useSelectedStore } = await import(
          "@/composables/useSelectedStore"
        );
        // Create a store-like object for useSelectedStore
        const storeMock = {
          getters: rootGetters,
          state: rootState
        };
        const selectedStore = useSelectedStore(storeMock);

        const storeStatus = selectedStore.getStoreStatus();
        const currentConfig = rootGetters.orderConfig || {};

        // If store is in schedule-only mode, ensure timing is scheduled and set defaults
        if (storeStatus === "schedule_only") {
          const newConfig = {
            ...currentConfig,
            timing: "scheduled"
          };

          // Set default scheduledDay only if not already set
          // Don't override if it was already set (e.g., by setFirstAvailableDiningOption)
          if (!newConfig.scheduledDay || newConfig.scheduledDay.trim() === "") {
            newConfig.scheduledDay = "Today";
          }

          // Preserve scheduledTime if it was already set (e.g., by setFirstAvailableDiningOption)
          // Don't override it - only set if not already present
          if (
            !newConfig.scheduledTime ||
            newConfig.scheduledTime.trim() === ""
          ) {
            // Note: scheduledTime will be set by DeliverySchedulePicker component
            // when it calculates available times and selects the first one
            // But only if it's not already set
          }

          commit("SET_ORDER_CONFIG", newConfig);
          await dispatch("applyPromotions");
          commit("RECALCULATE", { rootGetters });
        }
      } catch (error) {
        console.warn("Failed to set default schedule:", error);
      }
    },

    // Calculations
    recalculate({ commit, rootGetters }) {
      commit("RECALCULATE", { rootGetters });
    },
    setTipAmount({ commit }, payload) {
      commit("setTipAmount", payload);
    },

    // Order details
    setServeAt({ commit }, payload) {
      commit("setServeAt", payload);
    },
    setDeliveryAddress({ commit }, payload) {
      commit("setDeliveryAddress", payload);
    }
  }
};
